import { formatCount } from './helpers';

interface ScorecardSessionProgressProps {
    featureCount: number | null | undefined;
    claimedFeatureCount: number | null | undefined;
    reviewedFeatureCount: number | null | undefined;
    draftCount: number | null | undefined;
}

const ScorecardSessionProgress = ({
    featureCount,
    claimedFeatureCount,
    reviewedFeatureCount,
    draftCount
}: ScorecardSessionProgressProps) => {
    const features = featureCount ?? 0;
    const claimed = claimedFeatureCount ?? 0;
    const reviewed = reviewedFeatureCount ?? 0;
    const drafts = draftCount ?? 0;

    // Features -> Claimed -> Reviewed is a funnel (reviewed <= claimed <= features), so render it as
    // overlaid progress layers against the feature total rather than three lookalike standalone numbers.
    const toPercent = (value: number): number => (features > 0 ? Math.min(100, Math.round((value / features) * 100)) : 0);
    const claimedPercent = toPercent(claimed);
    const reviewedPercent = toPercent(reviewed);

    return (
        <div className="rounded border border-zinc-800 bg-zinc-900 p-3">
            <div className="mb-2 flex items-end justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-xs text-zinc-500">Review progress</div>
                    <div className="truncate text-lg font-semibold text-white">
                        {formatCount(reviewed)}{' '}
                        <span className="text-sm font-normal text-zinc-500">of {formatCount(features)} features</span>
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-xs text-zinc-500">Drafts</div>
                    <div className="text-lg font-semibold text-white">{formatCount(drafts)}</div>
                </div>
            </div>

            <div className="relative h-2 w-full overflow-hidden rounded-full bg-zinc-800">
                <div
                    className="absolute inset-y-0 left-0 rounded-full bg-cyan-500/40 transition-all"
                    style={{ width: `${claimedPercent}%` }}
                />
                <div
                    className="absolute inset-y-0 left-0 rounded-full bg-green-500 transition-all"
                    style={{ width: `${reviewedPercent}%` }}
                />
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-400">
                <span className="flex items-center gap-1.5">
                    <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
                    Reviewed {formatCount(reviewed)} ({reviewedPercent}%)
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="inline-block h-2 w-2 rounded-full bg-cyan-500/60" />
                    Claimed {formatCount(claimed)} ({claimedPercent}%)
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="inline-block h-2 w-2 rounded-full bg-zinc-600" />
                    Features {formatCount(features)}
                </span>
            </div>
        </div>
    );
};

export default ScorecardSessionProgress;
